import React from 'react';
import { getOrCreateConversation } from '../../data/chat';
import ChatPanel from '../../components/chat/ChatPanel';
import { useToasts } from '../../ui/ToastProvider';

interface TransferOrderChatPanelProps {
  transferId: string;
  collapsed?: boolean;
  onUnreadChange?: (count: number) => void;
  transferSummary?: {
    docNo?: string | null;
    fromLocation?: string | null;
    toLocation?: string | null;
  };
}

export default function TransferOrderChatPanel({
  transferId,
  collapsed = false,
  onUnreadChange,
  transferSummary,
}: TransferOrderChatPanelProps) {
  const { push } = useToasts();
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await getOrCreateConversation('transfer_order', transferId);
        if (cancelled) return;
        if (error) {
          console.error('[transfer-chat] failed to get/create conversation', error);
          push({ type: 'error', message: 'Failed to load chat' });
          setLoading(false);
          return;
        }
        if (data) {
          setConversationId(data.conversation_id);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('[transfer-chat] error', err);
          push({ type: 'error', message: err?.message || 'Failed to load chat' });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transferId, push]);

  if (loading) {
    return (
      <div className="pad-sm">
        <div className="text-sm fg-muted">Loading chat...</div>
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div className="pad-sm">
        <div className="alert alert-error text-sm">Failed to initialize chat</div>
      </div>
    );
  }

  const summaryDocNo = transferSummary?.docNo ? String(transferSummary.docNo).trim() : '';
  const summaryFrom = transferSummary?.fromLocation ? String(transferSummary.fromLocation).trim() : '';
  const summaryTo = transferSummary?.toLocation ? String(transferSummary.toLocation).trim() : '';

  return (
    <ChatPanel
      conversationId={conversationId}
      collapsed={collapsed}
      onUnreadChange={onUnreadChange}
      headerTitle="Transfer Order Chat"
      headerSubtitle="Collaborate with your team about this transfer order."
      placeholder="Add a note for the transfer team..."
    />
  );
}
